import { useMutation } from '@apollo/client';
import type { UpdateUncontrolledRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateUncontrolledRiskInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateRiskInternalAuditResultInput } from './useUpdateControlledRiskInternalAuditResultTRPC';
import { useUpdateUncontrolledRiskInternalAuditResultTRPC } from './useUpdateUncontrolledRiskInternalAuditResultTRPC';

export const useUpdateUncontrolledRiskInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateUncontrolledRiskInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_uncontrolled_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'risk_uncontrolled_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateUncontrolledRiskInternalAuditResultTRPC();

  const updateUncontrolledRiskInternalAuditResult = async (
    variables: UpdateRiskInternalAuditResultInput
  ): Promise<UpdateUncontrolledRiskInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateUncontrolledRiskInternalAuditResult(variables);
    }

    const result = await updateGraphQL({ variables });
    if (!result.data) {
      throw new Error(
        'Failed to update uncontrolled risk internal audit result'
      );
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateUncontrolledRiskInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateUncontrolledRiskInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
